import React, { useEffect, useState } from 'react';
import { Box, Title, Table, Checkbox, TextInput, Button, Group } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { setMdmCurrentStep, setSelectedCardId, updateCards } from '../../stateManagement/actions/mdmDashboard';
import { Loader } from '../../components/loader/Loader';
import { Card, ExcelColumns, MdmColumnSetUpProps } from './mdmUtils';
import { openPopup } from '../../stateManagement/actions/popupActions';
import { Autocomplete, TextField } from '@mui/material';


const MdmColumnSetUp = ({ setExcelMasterColumns }: MdmColumnSetUpProps) => {
    const [enableActiveStatus, setEnableActiveStatus] = useState<{ [cardId: string]: boolean }>({});
    const [activeStatusKey, setActiveStatusKey] = useState<{ [cardId: string]: string }>({});    
    const [loading, setLoading] = useState<boolean>(false);
    const cards = useSelector((state: any) => state.mdmDashboardReducer.cards);

    const selectedCardIdFromStore = useSelector((state: any) => state.mdmDashboardReducer.selectedCardId);
    const selectedCardId = selectedCardIdFromStore ?? cards?.[0]?.id ?? null;
    const [excelColumns, setExcelColumns] = useState<ExcelColumns>({});
    const [columnMappings, setColumnMappings] = useState<any>({}); // { cardId: { columnName: { id, label, postMappingField } } }

    const fetchExcelTemplate = async (card: Card) => {
        setLoading(true);
        try {
            const res = await fetch(card.templateUrl);
            const data = await res.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            // const excelData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            // const columns = Array.isArray(excelData[0]) ? excelData[0] : [];
            const excelData = XLSX.utils.sheet_to_json(sheet, { header: 1, range: 2 });
            const columns = Array.isArray(excelData[0]) ? excelData[0].filter((col: any) => col !== undefined && col !== null && String(col).trim() !== '') : [];
            setExcelColumns((prev) => ({
                ...prev,
                [card.id]: columns.map((col: any) => String(col)),
            }));
        } catch (err) {
            console.error('Error fetching Excel:', err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const statusEnabled: { [cardId: string]: boolean } = {};
        const statusKeys: { [cardId: string]: string } = {};
      
        cards.forEach((card: Card) => {
          // Fetch Excel Template
          fetchExcelTemplate(card);
      
          // Initialize EnableActiveStatus and ActiveStatusKey
          statusEnabled[card.id] = card.actions?.includes("ChangeStatus") || false;
          statusKeys[card.id] = card.activeStatusKey || "";
        });
      
        setEnableActiveStatus(statusEnabled);
        setActiveStatusKey(statusKeys);
      }, [cards]);
      
    useEffect(() => {
        const initialMappings: any = {};
        cards.forEach((card: Card) => {
            const excelCols = excelColumns[card.id] || [];
            const configCols = card?.tableConfiguration?.columns || [];
            initialMappings[card.id] = {};

            const configColMap = configCols.reduce((acc, col) => {
                acc[col.columnName] = col;
                return acc;
            }, {} as Record<string, any>);

            excelCols.forEach((colName: string) => {
                const configCol = configColMap[colName];
                
                if (configCol) {
                    initialMappings[card.id][colName] = {
                        id: configCol.id,
                        label: configCol.label,
                        mandatory: configCol.mandatory || false,
                        showInTable: configCol.showInTable !== undefined ? configCol.showInTable : true,
                        tableField: configCol.tableField !== undefined ? configCol.tableField : '',
                        postMappingField: configCol.postMappingField || '',
                    };
                } else {
                    initialMappings[card.id][colName] = {
                        id: '',
                        label: '',
                        mandatory: false,
                        showInTable: false,
                        tableField: '',
                        postMappingField: '',
                    };
                }
            });
        });

        setColumnMappings(initialMappings);
        setExcelMasterColumns(excelColumns);
    }, [excelColumns, cards]);

    const handleColumnMappingChange = (cardId: string, colName: string, field: 'id' | 'label' | 'postMappingField', value: string) => {
        setColumnMappings((prev: any) => ({
            ...prev,
            [cardId]: {
                ...prev[cardId],
                [colName]: {
                    ...prev[cardId]?.[colName],
                    [field]: value,
                },
            },
        }));
    };
    const handleCheckboxChange = (cardId: string, colName: string, checked: boolean) => {
        setColumnMappings((prev: any) => {
            const col = prev[cardId]?.[colName];
            const isMandatory = col?.mandatory === true;
    
            if (!checked && isMandatory) {
                openPopup("Alert", "This column is mandatory and cannot be unchecked.");
                return prev;
            }
    
            return {
                ...prev,
                [cardId]: {
                    ...prev[cardId],
                    [colName]: {
                        ...col,
                        id: checked && (!col?.id || col.id === '') ? colName : col?.id || '',
                        label: checked && (!col?.label || col.label === '') ? colName : col?.label || '',
                        showInTable: checked,
                        tableField: checked ? (col?.tableField || '') : '',
                    },
                },
            };
        });
    };

    const handleSearchFieldValueChange = (cardId: string, colName: string, value: string) => {
        setColumnMappings((prev: any) => {
            const col = prev[cardId]?.[colName] || {};
            if (!col.showInTable) {
                return prev;
            }
            return {
                ...prev,
                [cardId]: {
                    ...prev[cardId],
                    [colName]: {
                        ...col,
                        tableField: value,
                    },
                },
            };
        });
    };

    const handleToggleAllColumns = (cardId: string, checked: boolean) => {
        setColumnMappings((prev: any) => {
            const next = { ...prev };
            const cols: string[] = (excelColumns[cardId] || []) as string[];
            const cardMap = { ...(next[cardId] || {}) };
            let triedUncheckMandatory = false;

            cols.forEach((colName) => {
                const col = cardMap[colName] || { id: '', label: '', mandatory: false, showInTable: false, tableField: '' };
                if (checked) {
                    cardMap[colName] = {
                        ...col,
                        showInTable: true,
                        id: col.id && col.id !== '' ? col.id : colName,
                        label: col.label && col.label !== '' ? col.label : colName,
                    };
                } else {
                    if (col.mandatory) {
                        triedUncheckMandatory = true;
                        cardMap[colName] = { ...col, showInTable: true };
                    } else {
                        cardMap[colName] = { ...col, showInTable: false, tableField: '' };
                    }
                }
            });

            if (triedUncheckMandatory) {
                openPopup("Alert", "Mandatory columns cannot be unchecked.");
            }

            next[cardId] = cardMap;
            return next;
        });
    };

    const handleSaveAndNext = () => {
        // const selectedCard = cards.find((card: Card) => card.id === selectedCardId);
        // if (!selectedCard) {
        //     openPopup("Alert", "No card selected");
        //     return;
        // }
        // const mappings = columnMappings[selectedCard.id] || {};
        // for (const [colName, mapping] of Object.entries(mappings)) {
        //     const { id, label, showInTable } = mapping as { 
        //         id: string; 
        //         label: string; 
        //         showInTable: boolean; 
        //     };

        //     if (showInTable && (!id?.trim() || !label?.trim())) {
        //         openPopup(
        //             "Alert",
        //             `Please fill both ID and Label for unmapped column in card "${selectedCard.name}"`
        //         );
        //         return;
        //     }
        // }

        const updatedCards = cards.map((card: Card) => {
            const mappedCols = columnMappings[card.id] || {};
            
            const newColumns = Object.entries(mappedCols).map(([columnName, value]) => {
              const { id, label, mandatory, showInTable, tableField, postMappingField } = value as { 
                id: string; 
                label: string; 
                mandatory: boolean; 
                showInTable: boolean; 
                tableField: string;
                postMappingField: string;
              };
              
              const columnConfig = {
                columnName,
                id,
                label,
                mandatory,
                showInTable,
                tableField,
                postMappingField,
              };
              
              return columnConfig;
            });
            const isActiveEnabled = enableActiveStatus[card.id];
            const newActions = new Set(card.actions || []);
            if (isActiveEnabled) {
              newActions.add("ChangeStatus");
            } else {
              newActions.delete("ChangeStatus");
            }
          
            const updatedCard = {
              ...card,
              tableConfiguration: {
                ...card.tableConfiguration,
                columns: newColumns,
              },
              actions: Array.from(newActions),
              activeStatusKey: activeStatusKey[card.id] || "", // from local state
            };
            
            return updatedCard;
          });
          
          updateCards(updatedCards);
          setMdmCurrentStep(3);
          
    };

    const selectedExcelCols = excelColumns[selectedCardId] || [];
    const selectedMappings = columnMappings[selectedCardId] || {};
    const selectedCount = selectedExcelCols.filter((c: string) => selectedMappings[c]?.showInTable).length;
    const totalCount = selectedExcelCols.length;
    const selectAllChecked = totalCount > 0 && selectedCount === totalCount;
    const selectAllIndeterminate = selectedCount > 0 && selectedCount < totalCount;

    return (
        <Box style={{ display: 'flex', height: '100%', padding: '0.5rem',gap:"20px"  }}>
            <Box style={{ width: 250,height: '100%',overflowY: 'scroll',border: '1px solid rgb(204, 204, 204)', borderRadius: '6px'}}>
                {cards.map((card: Card) => (
                    <Box
                        key={card.id}
                        p="sm"
                        style={{
                            borderBottom: '1px solid #ddd',
                            backgroundColor: selectedCardId === card.id ? '#e3f2fd' : undefined,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        onClick={() => setSelectedCardId(card.id)}
                    >
                        {card.name}
                    </Box>
                ))}
            </Box>

            <Box style={{ flexGrow: 1, padding: '1rem', border: '1px solid #ccc', borderRadius: 8, overflowY: 'auto', position: 'relative' }}>
                {selectedCardId && (
                    loading ? (
                        <Loader />
                    ) : (//Show in Table
                        <>
                                <Box mb="md" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    <Box style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <Checkbox
                                            label="Enable Active Status"
                                            checked={enableActiveStatus[selectedCardId] || false}
                                            onChange={(e) => {
                                                const checked = e.currentTarget.checked;
                                                setEnableActiveStatus((prev) => ({
                                                  ...prev,
                                                  [selectedCardId]: checked,
                                                }));
                                              }}
                                        />
                                    </Box>

                                    {enableActiveStatus[selectedCardId] && (
                                        <Autocomplete
                                            sx={{ width: "40%" }}
                                            options={excelColumns[selectedCardId] ?? []}
                                            value={activeStatusKey[selectedCardId] || ''}
                                            onChange={(event, newValue) => {
                                                setActiveStatusKey((prev) => ({
                                                  ...prev,
                                                  [selectedCardId]: newValue || "",
                                                }));
                                              }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select Active Status Key" placeholder="Choose column" />
                                            )}
                                            fullWidth
                                        />
                                    )}
                                </Box>
                            <Box mb="md" p="xs" style={{ background: '#fff' }}>
                                <Box>
                                    <Group spacing="xs" mb="xs">
                                        <span>🟩 Green - Column mapping is successful</span>
                                    </Group>
                                    <Group spacing="xs">
                                        <span>🟥 Red - Column mismatch or unmapped column</span>
                                    </Group>
                                </Box>
                            </Box>
                            <Title order={4} mb="md">
                            Column Summary: {cards.find((card: Card) => card.id === selectedCardId)?.name}
                            </Title>
                            <Box mb="lg">
                                <Table striped>
                                    <thead>
                                        <tr>
                                            <th>Uploaded Excel Column</th>
                                            <th>ID</th>
                                            <th>Label</th>
                                            <th>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span>Table View</span>
                                                    <Checkbox
                                                        checked={selectAllChecked}
                                                        indeterminate={selectAllIndeterminate}
                                                        onChange={(e) => handleToggleAllColumns(selectedCardId, e.currentTarget.checked)}
                                                    />
                                                </div>
                                            </th>
                                            <th>Table Id</th>
                                            <th>Post Mapping Field</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excelColumns[selectedCardId]?.map((colName: string) => {
                                            const excelMappedColumnNames = cards.find((c: { id: any; }) => c.id === selectedCardId)?.tableConfiguration?.columns?.map((c: { columnName: any; }) => c.columnName) || [];
                                            const isExcelMatch = excelMappedColumnNames.includes(colName); 
                                            const mapping = columnMappings[selectedCardId]?.[colName] || { 
                                                id: colName, 
                                                label: colName, 
                                                mandatory: false, 
                                                showInTable: false,
                                                tableField: '',
                                                postMappingField: '',
                                            };
                                            const isMandatory = mapping.mandatory || false; // Check if the column is mandatory
                                            const showInTable = mapping.showInTable || false; // Check if column should show in table

                                            return (
                                                <tr
                                                key={colName}
                                                style={{
                                                  backgroundColor: isExcelMatch ? '#e6f4ea' : '#fdecea',
                                                }}
                                              >
                                                    <td>{colName}</td>
                                                    <td>
                                                        <TextInput
                                                            value={mapping.id}
                                                            onChange={(e) =>
                                                                handleColumnMappingChange(selectedCardId, colName, 'id', e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextInput
                                                            value={mapping.label}
                                                            onChange={(e) =>
                                                                handleColumnMappingChange(selectedCardId, colName, 'label', e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Checkbox
                                                            checked={showInTable}
                                                            onChange={(e) =>
                                                                handleCheckboxChange(selectedCardId, colName, e.target.checked)
                                                            }
                                                            color={isMandatory ? 'gray' : 'blue'}
                                                        />
                                                    </td>
                                                    <td>
                                                        {showInTable && (
                                                            <TextInput
                                                                value={mapping.tableField || ''}
                                                                onChange={(e) => handleSearchFieldValueChange(selectedCardId, colName, e.target.value)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <TextInput
                                                            value={mapping.postMappingField || ''}
                                                            onChange={(e) =>
                                                                handleColumnMappingChange(selectedCardId, colName, 'postMappingField', e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Box>
                            <Box style={{ position: 'sticky', bottom: 0, padding: '0.1rem', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', zIndex: 5 }}>
                              <Button style={{ width: 'fit-content' }} onClick={handleSaveAndNext}>Save and Next</Button>
                            </Box>
                        </>
                    )
                )}
                <Group position="right" style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>
                    <Button onClick={() => setMdmCurrentStep(1)}>Back</Button>
                </Group>
            </Box>
        </Box>
    );
};

export default MdmColumnSetUp;
